import { Capacitor } from '@capacitor/core'
import {
    LocalNotifications,
    LocalNotificationSchema,
} from '@capacitor/local-notifications'
import {
    AppUser,
    IItinerarymap,
    PayloadEffects,
    spLogTags,
    UpNextTimelineEntrySource,
} from '@eventr/common'
import _ from 'lodash'
import AppGroupUserDefaults from '../../plugins/appGroupUserDefaults/AppGroupUserDefaultsPlugin'
import { bs, useBoundStore } from '../../state/bound.store'
import { IItinerary } from '../../state/interfaces'
import { transformItineraryComponents } from '../../state/itineraryState/currentItin.slice'
import { replaceRenderOptions } from '../../state/itineraryState/itineraryMap.slice'
import { userPrefShowDestTime } from '../../state/userPrefsState/userPrefsSignels'

export function entryGenerator(selectedData: {
    itineraryData: {
        [key: string]: IItinerary
    }
    scheduleLocalNotifications: boolean
    notificationPermissionsGranted: boolean
    notificationFilters: {
        [key: number]: number[]
    }
    itineraryMap: IItinerarymap[]
}) {
    const {
        itineraryData,
        scheduleLocalNotifications,
        notificationPermissionsGranted,
        notificationFilters,
        itineraryMap,
    } = selectedData
    const wpUser = bs().wpUser

    if (wpUser) {
        const entries = getAllEntries(
            notificationFilters,
            itineraryData,
            itineraryMap,
            wpUser
        )

        spLogTags(`${entries.length} entries parsed`, ['parsing'])

        if (
            scheduleLocalNotifications &&
            notificationPermissionsGranted &&
            Capacitor.getPlatform() !== 'web'
        )
            scheduleNotifications(entries, itineraryMap)

        syncWidget(entries)
    } else {
        spLogTags('User logged out, cannot sync widget', [
            'ERROR',
            'localNotifications',
            'sync',
            'widget',
        ])
    }
}

function syncWidget(
    entries: (UpNextTimelineEntrySource & { itineraryId: number })[]
) {
    spLogTags('SYNCING WIDGET', ['sync', 'widget'])
    const subset = sortEntries(entries).slice(0, 20)

    spLogTags(subset, ['sync', 'widget'])
    spLogTags(bs().timelineEntries, ['sync', 'widget'])
    if (
        Capacitor.getPlatform() == 'ios' &&
        !_.isEqual(bs().timelineEntries, subset)
    ) {
        spLogTags(JSON.stringify(subset), ['sync', 'widget'])

        AppGroupUserDefaults.set({
            key: 'itinerary',
            value: JSON.stringify(subset),
            reloadAllTimelines: true,
        })

        // Update cached payload in the store
        useBoundStore.setState({
            timelineEntries: subset,
        })
    }
}

function sortEntries(
    entries: (UpNextTimelineEntrySource & { itineraryId: number })[]
): (UpNextTimelineEntrySource & { itineraryId: number })[] {
    function getEntryTime(
        entry1: UpNextTimelineEntrySource,
        entry2: UpNextTimelineEntrySource
    ): number {
        if (entry2 && entry1) {
            if (entry2.startOrEnd === 'start') {
                return entry1.date && entry1.date?.timestamp
                    ? entry1.date.timestamp
                    : 0
            } else if ('checkIn' in entry1) {
                if (entry1.checkIn && entry1.checkIn.timestamp) {
                    return entry1.checkIn.timestamp
                } else {
                    return entry1.date && entry1.date?.timestamp
                        ? entry1.date.timestamp
                        : 0
                }
            } else {
                return entry1.date && entry1.date?.timestamp
                    ? entry1.date.timestamp
                    : 0
            }
        } else {
            return 0
        }
    }
    return entries.sort((a, b) => {
        return getEntryTime(a, a) - getEntryTime(a, b)
    })
}

function scheduleNotifications(
    entries: (UpNextTimelineEntrySource & { itineraryId: number })[],
    itineraryMap: IItinerarymap[]
) {
    const notifications: {
        title: string
        body: string
        timestamp: number
        id: string
    }[] = []

    sortEntries(entries)
        .slice(0, 1000)
        .forEach(
            (entry: UpNextTimelineEntrySource & { itineraryId: number }) => {
                const title = itineraryMap.find(
                    (itineraryMapElement) =>
                        itineraryMapElement.id === entry.itineraryId
                )?.itinerary_name

                if (!title) return

                if (entry.section && entry.date && entry.date.timestamp) {
                    notifications.push({
                        title,
                        body:
                            getLabel(entry.section, entry.startOrEnd) +
                            ' ' +
                            entry.primary,
                        timestamp: entry.date.timestamp,
                        id: `${entry.itineraryId}_${entry.section}_${entry.id}`,
                    })
                }

                if (entry.checkIn?.timestamp && entry.startOrEnd === 'start') {
                    notifications.push({
                        title,
                        body: 'Check-in: ' + entry.primary,
                        timestamp: entry.checkIn.timestamp,
                        id: `${entry.itineraryId}_${entry.section}_${entry.id}_${entry.startOrEnd}`,
                    })
                }
            }
        )

    const localNotifications: LocalNotificationSchema[] = notifications.map(
        (notification, index) => {
            return {
                id: index,
                title: notification.title,
                body: notification.body,
                schedule: {
                    at: new Date(notification.timestamp),
                },
                extra: { id: notification.id },
            }
        }
    )

    // Cancel all notifications before scheduling new ones
    LocalNotifications.getPending().then((res) => {
        const pending = res.notifications ?? []
        localNotifications.forEach((n) => {
            if ((n.schedule?.at?.valueOf() ?? 0) < new Date().getTime()) {
                return
            }
            const found = pending.find((r) => r.extra?.id === n.extra?.id)

            if (found) {
                if (
                    found.body !== n.body ||
                    found.title !== n.title ||
                    found.schedule?.at?.valueOf() !== n.schedule?.at?.valueOf()
                ) {
                    LocalNotifications.cancel({
                        notifications: [{ id: found.id }],
                    }).then(() => {
                        LocalNotifications.schedule({
                            notifications: [n],
                        })
                    })
                }
            } else {
                LocalNotifications.schedule({
                    notifications: [n],
                })
            }
        })
    })
}

function getLabel(
    section:
        | 'flight'
        | 'accommodation'
        | 'transport'
        | 'general-activity'
        | 'parking'
        | 'hire-car',
    startOrEnd?: 'start' | 'end'
): string {
    switch (section) {
        case 'flight':
        case 'transport':
            return startOrEnd === 'start' ? 'Departs:' : 'Arrives:'

        case 'accommodation':
            return startOrEnd === 'start' ? 'Check-in:' : 'Check-out:'

        case 'general-activity':
            return startOrEnd === 'start' ? 'Starts:' : 'Ends:'

        case 'parking':
            return startOrEnd === 'start' ? 'Entry:' : 'Exit:'

        case 'hire-car':
            return startOrEnd === 'start' ? 'Pick-up:' : 'Drop-off:'
    }
}

function getAllEntries(
    hidden: {
        [key: number]: number[]
    },
    itineraryData: { [key: string]: IItinerary },
    itineraryMap: IItinerarymap[],
    user: AppUser
): (UpNextTimelineEntrySource & { itineraryId: number })[] {
    const entries = itineraryMap
        .filter((itineraryMapElement) => {
            const isDownloaded = Object.keys(itineraryData).includes(
                itineraryMapElement.id.toString()
            )
            const isInTheFuture =
                itineraryMapElement.end_time * 1000 >= new Date().getTime()

            return isDownloaded && isInTheFuture
        })
        .map((itineraryMapElement) => {
            const hiddenSectionIds = hidden[itineraryMapElement.id] ?? []
            const itinerary = replaceRenderOptions(
                itineraryData[itineraryMapElement.id]
            )

            itinerary.v2 = itinerary.v2?.filter((section) => {
                return (
                    !hiddenSectionIds.includes(section.sectionID) ||
                    section.sectionID === 1
                )
            })

            const itineraryComponents = transformItineraryComponents(
                itinerary,
                userPrefShowDestTime.value,
                user
            )

            try {
                const entries = PayloadEffects(user.id, 1000).parse(
                    itineraryComponents
                )

                return entries.map((entry) => {
                    return {
                        ...entry,
                        itineraryId: itineraryMapElement.id,
                    }
                })
            } catch (error) {
                spLogTags(error, ['ERROR', 'parsing'])
            }

            return []
        })
        .flat()

    return entries as (UpNextTimelineEntrySource & { itineraryId: number })[]
}
